/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import DynamicFieldsFragment from '@linc-technologies/linced-common/models/dynamic-field/base-fragment';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import RelationshipEnum from '@linc-technologies/linced-common/enums/contact-relationship';
import { peekPerson } from '@linc-technologies/linced-common/utils/load-people';
import type Person from 'linced-app/models/person';

// Proto: people.Contact.
export default class Contact extends DynamicFieldsFragment {
  declare [Type]: 'people/contact';

  @attr('string', { defaultValue: '' })
  declare caregiverId: string;

  // caregiver reads the caregiver's person from the store. It does not load
  // it. The route or the component must load the caregivers before render
  // (loadPeople, or a type: CAREGIVER query for the whole school).
  get caregiver() {
    if (isEmpty(this.caregiverId)) {
      return null;
    }
    return peekPerson<Person>(
      this.store,
      this.caregiverId,
      'Load the caregivers in the route afterModel (loadPeople, or a type: CAREGIVER query) or in the task that opens the view.',
    );
  }

  @attr('number', { defaultValue: 9 })
  declare priority: number; // If not set from 1-9, default to 9 so they come last.

  @computed('caregiver.fullName')
  get fullName() {
    return this.get('caregiver.fullName');
  }

  @computed('caregiver.fields.core:emailAddress.value')
  get emailAddress() {
    return this.get('caregiver.fields.core:emailAddress.value');
  }

  @alias('caregiver.fields.core:phoneMobile.value')
  declare phoneMobile: unknown;

  @attr('number', { defaultValue: 0 })
  declare type: number;

  @attr('number', { defaultValue: 0 })
  declare relationship: number;

  @computed('relationship')
  get relationshipName() {
    return RelationshipEnum.getLabel(this.relationship);
  }

  @attr('string', { defaultValue: '' })
  declare notes: string;

  @attr('boolean', { defaultValue: false })
  declare allowedAccess: boolean;

  @attr('boolean', { defaultValue: false })
  declare receivesSms: boolean;

  @attr('boolean', { defaultValue: false })
  declare billPayer: boolean;

  // hasCustodyOrder provides a shortcut to check whether either of the NSW
  // custody order fields is set for this contact.
  @computed(
    'fields.{core:custodyRestriction.valueBool,core:nonAVOCustodyRestriction.valueBool}',
  )
  get hasCustodyOrder() {
    const avoRestriction = this.get('fields.core:custodyRestriction.valueBool');
    const nonAvoRestriction = this.get(
      'fields.core:nonAVOCustodyRestriction.valueBool',
    );

    return avoRestriction || nonAvoRestriction;
  }
}
